import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchClients, archiverClient, activerClient } from '../store/slices/clientsSlice';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { toast } from 'react-toastify';
import { Plus, Search, UserX, RotateCcw, Eye, Pencil, Users, UserCheck, UserMinus, CalendarClock, FileSpreadsheet, FileText, ChevronDown, Archive, GraduationCap, Briefcase } from 'lucide-react';
import api from '../services/api';

const STATUT_BADGE = { actif: 'vert', inactif: 'orange', archive: 'gris' };
const STATUT_LABEL = { actif: 'Actif', inactif: 'Inactif', archive: 'Archivé' };
const SEXE_LABEL   = { masculin: '♂ M', feminin: '♀ F' };

export function ClientsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { liste: list, loading } = useSelector(s => s.clients);
  const { kpis } = useSelector(s => s.dashboard);

  const [search,         setSearch]         = useState('');
  const [filterStatuts,  setFilterStatuts]  = useState(() => new Set());
  const [filterOrigine,  setFilterOrigine]  = useState('tous');
  const [exportMenu,     setExportMenu]     = useState(false);
  const [exporting,      setExporting]      = useState(false);

  const toggleStatut = (v) => setFilterStatuts(s => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n; });

  const params = { inclure_archives: true };

  /* Chargement initial + auto-refresh 60s */
  useEffect(() => { dispatch(fetchClients(params)); }, [dispatch]);
  useAutoRefresh(() => fetchClients(params), 60000);

  /* ── Filtrage universel ── */
  const filtered = (list || []).filter(c => {
    if (filterStatuts.size > 0 && !filterStatuts.has(c.statut_cli)) return false;
    if (filterOrigine === 'formation' && c.origine_cli === 'coaching') return false;
    if (filterOrigine === 'coaching'  && c.origine_cli === 'formation') return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (`${c.nom_cli} ${c.prenom_cli}`).toLowerCase().includes(q) ||
      (c.nom_jeune_fille     || '').toLowerCase().includes(q) ||
      (c.telephone_principal || '').includes(q) ||
      (c.email_cli           || '').toLowerCase().includes(q) ||
      (c.fonction            || '').toLowerCase().includes(q) ||
      (c.numero_identite     || '').toLowerCase().includes(q) ||
      (c.commercial_nom      || '').toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) =>
    `${a.nom_cli} ${a.prenom_cli}`.localeCompare(`${b.nom_cli} ${b.prenom_cli}`, 'fr')
  );

  const handleArchiver = async (id, nom) => {
    if (!window.confirm(`Archiver le client ${nom} ?`)) return;
    try {
      await dispatch(archiverClient(id)).unwrap();
      toast.success('Client archivé.');
      dispatch(fetchClients(params));
    } catch {
      toast.error("Erreur lors de l'archivage.");
    }
  };

  const handleActiver = async (id, nom) => {
    if (!window.confirm(`Réactiver le client ${nom} ?`)) return;
    try {
      await dispatch(activerClient(id)).unwrap();
      toast.success('Client réactivé.');
      dispatch(fetchClients(params));
    } catch {
      toast.error("Erreur lors de la réactivation.");
    }
  };

  const exportCSV = () => {
    setExportMenu(false);
    const headers = ['Nom', 'Prénom', 'Nom jeune fille', 'Sexe', 'Date naissance', 'Anniversaire',
      'Type pièce', 'N° pièce', 'Téléphone', 'Email', 'Fonction', 'Statut', 'Date inscription', 'Commercial', 'Coachings actifs'];
    const rows = sorted.map(c => [
      c.nom_cli, c.prenom_cli, c.nom_jeune_fille || '',
      c.sexe_cli === 'masculin' ? 'Masculin' : c.sexe_cli === 'feminin' ? 'Féminin' : '',
      c.date_naissance_cli ? new Date(c.date_naissance_cli).toLocaleDateString('fr-FR') : '',
      anni(c.date_naissance_cli),
      c.type_identite === 'cni' ? 'CNI' : c.type_identite === 'passeport' ? 'Passeport' : '',
      c.numero_identite || '',
      c.telephone_principal, c.email_cli || '', c.fonction || '',
      STATUT_LABEL[c.statut_cli] || c.statut_cli,
      c.date_inscription_cli ? new Date(c.date_inscription_cli).toLocaleDateString('fr-FR') : '',
      c.commercial_nom || '',
      c.nb_coachings_actifs ?? 0,
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `clients_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${sorted.length} client(s) exporté(s) en CSV.`);
  };

  const exportPDF = async () => {
    setExportMenu(false);
    setExporting(true);
    try {
      const params = {};
      if (filterStatuts.size === 1) params.statut_cli = [...filterStatuts][0];
      if (search) params.search = search;
      const response = await api.get('/clients/export/pdf/', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a   = document.createElement('a');
      a.href = url;
      a.download = `clients_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export PDF téléchargé.`);
    } catch { toast.error("Erreur lors de l'export PDF."); }
    finally  { setExporting(false); }
  };

  const fmt  = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const anni = (d) => {
    if (!d) return '—';
    const dn = new Date(d);
    return `${String(dn.getDate()).padStart(2,'0')}/${String(dn.getMonth()+1).padStart(2,'0')}`;
  };

  return (
    <div className="page-list">
      {/* ── Barre unique : titre + recherche + bouton ── */}
      <div style={{ marginBottom: 16 }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {/* Recherche universelle */}
          <div className="search-bar" style={{ flex: '1 1 200px', minWidth: 200 }}>
            <Search size={14} />
            <input
              placeholder="Nom, téléphone, email, statut, commercial…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ fontSize: 12 }}
            />
          </div>

          <div style={{ width: 1, height: 28, background: '#E5E7EB', flexShrink: 0 }} />

          {/* Export */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              className="header-btn btn-secondary"
              onClick={() => setExportMenu(v => !v)}
              disabled={exporting}
              style={{ height: 36, padding: '0 12px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}
            >
              {exporting ? 'Export…' : 'Exporter'} <ChevronDown size={13} />
            </button>
            {exportMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setExportMenu(false)} />
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100,
                  minWidth: 220, overflow: 'hidden',
                }}>
                  <div style={{ padding: '7px 12px', fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #F3F4F6' }}>
                    Exporter la sélection ({sorted.length})
                  </div>
                  {[
                    { fn: exportCSV, Icon: FileSpreadsheet, color: '#1E7145', label: 'Exporter en CSV / Excel', sub: 'Fichier .csv — ouvre dans Excel' },
                    { fn: exportPDF, Icon: FileText,        color: '#C00000', label: 'Exporter en PDF',         sub: 'Document prêt à imprimer' },
                  ].map(({ fn, Icon, color, label, sub }, idx) => (
                    <div key={label}>
                      {idx > 0 && <div style={{ height: 1, background: '#F3F4F6' }} />}
                      <button
                        onClick={fn}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#1F2937', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <Icon size={16} color={color} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{label}</div>
                          <div style={{ fontSize: 11, color: '#6B7280' }}>{sub}</div>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <button
            className="header-btn btn-primary"
            onClick={() => navigate('/clients/nouveau')}
            style={{ height: 36, padding: '0 14px', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
          >
            <Plus size={15} /> Nouveau client
          </button>
        </div>
        {/* Ligne 2 : filtres */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          padding: '6px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, marginTop: 8 }}>

          {/* Statut */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>Statut :</span>
            {[
              { value: 'actif',    label: 'Actif',    color: '#1E7145' },
              { value: 'inactif',  label: 'Inactif',  color: '#ED7D31' },
              { value: 'archive',  label: 'Archivé',  color: '#6B7280' },
            ].map(({ value, label, color }) => (
              <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                fontSize: 12, fontWeight: filterStatuts.has(value) ? 700 : 400,
                color: filterStatuts.has(value) ? color : '#6B7280', userSelect: 'none' }}>
                <input type="checkbox" checked={filterStatuts.has(value)} onChange={() => toggleStatut(value)}
                  style={{ accentColor: color, cursor: 'pointer', width: 14, height: 14 }} />
                {label}
              </label>
            ))}
          </div>

          <div style={{ width: 1, height: 20, background: '#E5E7EB', flexShrink: 0 }} />

          {/* Origine */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>Origine :</span>
            {[
              { value: 'tous',      label: 'Tous',             Icon: Users,         activeColor: '#1F4E79', activeBg: '#EFF6FF' },
              { value: 'formation', label: 'Via formation',    Icon: GraduationCap, activeColor: '#6A0DAD', activeBg: '#F3E8FF' },
              { value: 'coaching',  label: 'Direct (coaching)', Icon: Briefcase,    activeColor: '#1E7145', activeBg: '#ECFDF5' },
            ].map(({ value, label, Icon, activeColor, activeBg }) => (
              <button key={value} onClick={() => setFilterOrigine(value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px',
                  borderRadius: 6, fontSize: 12, fontWeight: filterOrigine === value ? 700 : 400,
                  cursor: 'pointer', border: `1.5px solid ${filterOrigine === value ? activeColor : '#E5E7EB'}`,
                  background: filterOrigine === value ? activeBg : 'white',
                  color: filterOrigine === value ? activeColor : '#6B7280',
                  transition: 'all 0.15s',
                }}>
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>

          {(filterStatuts.size > 0 || filterOrigine !== 'tous') && (
            <button onClick={() => { setFilterStatuts(new Set()); setFilterOrigine('tous'); }}
              style={{ fontSize: 11, color: '#C00000', background: 'none', border: 'none',
                cursor: 'pointer', textDecoration: 'underline', padding: 0, marginLeft: 'auto' }}>
              Tout afficher
            </button>
          )}
        </div>
      </div>

      {/* ── Totaux ── */}
      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 14 }}>
          {[
            { label: 'Total clients',      value: (list||[]).length,                                                              icon: Users,        color: '#1F4E79' },
            { label: 'Actifs',             value: (list||[]).filter(c=>c.statut_cli==='actif').length,                            icon: UserCheck,    color: '#1E7145' },
            { label: 'Via formation',      value: (list||[]).filter(c=>c.origine_cli==='formation'||c.origine_cli==='les_deux').length, icon: GraduationCap, color: '#6A0DAD' },
            { label: 'Direct (coaching)',  value: (list||[]).filter(c=>c.origine_cli==='coaching').length,                        icon: Briefcase,    color: '#2E75B6' },
            { label: 'Échéances ≤ 15 j',  value: kpis?.echeances_j15 ?? '—',                                                    icon: CalendarClock,color: '#C00000' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="card" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: color + '18', borderRadius: 8, padding: 8, flexShrink: 0 }}>
                <Icon size={18} color={color} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6B7280' }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', lineHeight: 1.2 }}>{value}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Tableau ── */}
      <div className="table-container">
        {loading ? (
          <div className="spinner"><div className="spinner-ring" /> Chargement...</div>
        ) : (
          <table style={{ width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 34   }} /> {/* N° */}
              <col style={{ width: '20%' }} /> {/* Nom / Prénom */}
              <col style={{ width: '10%' }} /> {/* Nom jeune fille */}
              <col style={{ width: 42   }} /> {/* Sexe */}
              <col style={{ width: 88   }} /> {/* Naissance */}
              <col style={{ width: '12%' }} /> {/* N° Identité */}
              <col style={{ width: '13%' }} /> {/* Téléphone */}
              <col style={{ width: '15%' }} /> {/* Email */}
              <col style={{ width: 70   }} /> {/* Statut */}
              <col style={{ width: 70   }} /> {/* Coachings */}
              <col style={{ width: 74   }} /> {/* Actions */}
            </colgroup>
            <thead>
              <tr>
                <th style={{ fontSize: 11 }}>N°</th>
                <th style={{ fontSize: 11 }}>Nom / Prénom</th>
                <th style={{ fontSize: 11 }}>Nom J.F.</th>
                <th style={{ fontSize: 11 }}>Sexe</th>
                <th style={{ fontSize: 11 }}>Naissance</th>
                <th style={{ fontSize: 11 }}>Pièce / N°</th>
                <th style={{ fontSize: 11 }}>Téléphone</th>
                <th style={{ fontSize: 11 }}>Email</th>
                <th style={{ fontSize: 11 }}>Statut</th>
                <th style={{ fontSize: 11 }}>Coachings</th>
                <th style={{ fontSize: 11 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: 32, color: '#6B7280' }}>
                    {search || filterStatuts.size > 0 ? 'Aucun résultat pour ce filtre' : 'Aucun client trouvé'}
                  </td>
                </tr>
              ) : sorted.map((c, i) => (
                <tr key={c.id} style={{ opacity: c.statut_cli === 'archive' ? 0.55 : 1 }}>
                  <td style={{ color: '#1F4E79', fontSize: 11, fontWeight: 700, padding: '8px 6px' }}>{i + 1}</td>
                  <td style={{ padding: '8px 6px', overflow: 'hidden' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.nom_cli} {c.prenom_cli}
                    </div>
                    <div style={{ fontSize: 10, color: '#6B7280' }}>{c.commercial_nom || '—'}</div>
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {c.nom_jeune_fille || <span style={{ color: '#9CA3AF' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 4px', textAlign: 'center' }}>
                    {SEXE_LABEL[c.sexe_cli] || '—'}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px' }}>{fmt(c.date_naissance_cli)}</td>
                  <td style={{ fontSize: 11, padding: '8px 6px', overflow: 'hidden' }}>
                    {c.type_identite || c.numero_identite ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {c.type_identite && (
                          <span style={{
                            fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                            background: c.type_identite === 'cni' ? '#EBF3FB' : '#EDE9FE',
                            color: c.type_identite === 'cni' ? '#1F4E79' : '#5B21B6',
                            textTransform: 'uppercase', letterSpacing: 0.3, alignSelf: 'flex-start',
                          }}>
                            {c.type_identite === 'cni' ? 'CNI' : 'PASSEPORT'}
                          </span>
                        )}
                        {c.numero_identite && (
                          <span style={{ fontSize: 11, color: '#374151', whiteSpace: 'nowrap' }}>
                            {c.numero_identite}
                          </span>
                        )}
                      </div>
                    ) : <span style={{ color: '#9CA3AF' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px' }}>{c.telephone_principal}</td>
                  <td style={{ fontSize: 11, padding: '8px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {c.email_cli || <span style={{ color: '#9CA3AF' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <span className={`badge badge-${STATUT_BADGE[c.statut_cli] || 'gris'}`} style={{ fontSize: 10 }}>
                      {STATUT_LABEL[c.statut_cli] || c.statut_cli}
                    </span>
                  </td>
                  <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                      {/* Badge origine */}
                      {c.origine_cli === 'formation' && (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                          background: '#F3E8FF', color: '#6A0DAD', display:'flex', alignItems:'center', gap:3 }}>
                          <GraduationCap size={9} /> Formation
                        </span>
                      )}
                      {c.origine_cli === 'coaching' && (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                          background: '#EFF6FF', color: '#2E75B6', display:'flex', alignItems:'center', gap:3 }}>
                          <Briefcase size={9} /> Direct
                        </span>
                      )}
                      {c.origine_cli === 'les_deux' && (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                          background: '#ECFDF5', color: '#1E7145', display:'flex', alignItems:'center', gap:3 }}>
                          Formation + Coaching
                        </span>
                      )}
                      {/* Coachings actifs */}
                      {c.nb_coachings_actifs > 0
                        ? <span className="badge badge-bleu" style={{ fontSize: 9 }}>{c.nb_coachings_actifs} coaching(s)</span>
                        : null}
                    </div>
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="header-btn btn-secondary btn-sm btn-icon"
                        onClick={() => navigate(`/clients/${c.id}`)} title="Voir la fiche">
                        <Eye size={13} />
                      </button>
                      <button className="header-btn btn-secondary btn-sm btn-icon"
                        onClick={() => navigate(`/clients/${c.id}/modifier`)} title="Modifier">
                        <Pencil size={13} />
                      </button>
                      {c.statut_cli === 'archive' ? (
                        <button className="header-btn btn-secondary btn-sm btn-icon"
                          onClick={() => handleActiver(c.id, `${c.prenom_cli} ${c.nom_cli}`)}
                          title="Réactiver le client">
                          <RotateCcw size={13} />
                        </button>
                      ) : (
                        <button className="header-btn btn-danger btn-sm btn-icon"
                          onClick={() => handleArchiver(c.id, `${c.prenom_cli} ${c.nom_cli}`)}
                          title="Archiver le client">
                          <UserX size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ClientsPage;
